import {
  ButtonFinal,
  ChoiceGroupCard,
  CountdownTimer,
  GroupUserData,
  GroupUserSearch,
  GroupUserSearchSortMethod,
  Icons,
  LayoutType,
  LeaderMessage,
  LeaderMessageProps,
  LeaderVote,
  ReactIcons,
  Text,
} from "@kalyzee/escaet-ui";
import { UserOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { Touchable } from "@kalyzee/kast-web-sdk/web-components";
import { Meeting, MeetingRoomHighlightingState, MeetingRoomOnUpdateEvent, MeetingRoomStatus } from "@kalyzee/kast-web-sdk/websocket";
import { useEffect, useMemo, useRef, useState } from "react";
import { callModeratorFromRoom, cancelModeratorCallFromRoom, requestRoomHighlight, stopRoomHighlight } from "../../../helpers/meeting";
import { sdkClient } from "../../../helpers/sdk";
import { toastInfo, toastSuccess } from "../../../helpers/toast";
import { useSocketAppDispatch } from "../../../hooks/app";
import { useEscaetGroupData } from "../../../hooks/meetingRoom";
import { useReduxAction } from "../../../hooks/store";
import { PopulatedMeetingData, PopulatedMeetingUser, StudentView } from "../../../interfaces/populatedData";
import socketUserMeetingRoomsActions from "../../../store/meeting-room/actions";
import { GoTo } from "../../navigation/GoTo";
import { PageMasterMode } from "../../navigation/PageContext";
import { RemotesToExpertsContainer } from "../../player/container/RemotesToExpertsContainer";
import styles from "./GroupStudentContainer.module.css";

export interface GroupStudentContainerProps {
  meeting: Meeting;
  populatedMeeting: PopulatedMeetingData;
  view: StudentView;
  selfUser: UserOutputDTO;
  layout: LayoutType;
  selfVideoStream?: MediaStream;
}

export const GroupStudentContainer = ({ meeting, populatedMeeting, view, selfUser, layout, selfVideoStream }: GroupStudentContainerProps) => {
  const dispatch = useSocketAppDispatch();

  const currentGroupLeaderRef = useRef<PopulatedMeetingUser>();

  const [selectedGroupId, setSelectedGroupId] = useState<string>();
  const [searchOpen, setSearchOpen] = useState(false);
  const [sortMethod, setSortMethod] = useState<GroupUserSearchSortMethod>("name");

  const { users, rooms } = populatedMeeting;

  const [tempInfo, setTempInfo] = useState<{
    message: string;
    userIcon: string;
    mode: LeaderMessageProps["mode"];
  }>();

  const { groups: escaetGroups, users: escaetUsers } = useEscaetGroupData(rooms.groups, users, selfUser.id);

  useEffect(() => {
    if (tempInfo) {
      const timeout = setTimeout(() => {
        setTempInfo(undefined);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [tempInfo]);

  // Fake :
  // const escaetGroups = Array.from({ length: 3 }).map((_, index) => ({
  //   id: index.toString(),
  //   title: `Groupe ${index}`,
  //   capacity: 5,
  //   userIds: new Array(5).map((_, i) => (i + index).toString()),
  // }));

  useReduxAction<MeetingRoomOnUpdateEvent>(
    (action) => {
      const { payload } = action;
      if (payload.updateType === "leader_vote_end") {
        onVoteEnd(payload.room.id, payload.updateData.leaderUserId);
      } else if (payload.updateType === "leader_updated") {
        onLeaderUpdate(payload.room.id, payload.updateData.leaderUserId);
      } else if (payload.updateType === "user_enlisted") {
        onUserEnlisted(payload.room.id, payload.updateData.userId);
      } else if (payload.updateType === "user_delisted") {
        onUserDelisted(payload.room.id, payload.updateData.userId);
      } else if (payload.updateType === "moderator_call") {
        onModeratorCall(payload.room.id, payload.updateData?.callingModerator);
      } else if (payload.updateType === "moderator_call_accepted") {
        onModeratorAnswered(payload.room.id, true);
      } else if (payload.updateType === "moderator_call_rejected") {
        onModeratorAnswered(payload.room.id, false);
      }
    },
    socketUserMeetingRoomsActions.onUpdate,
    [escaetUsers, escaetGroups]
  );

  useEffect(() => {
    const currentLeaderId = view.selfGroup?.room.leaderUserId;
    const currentLeader = currentLeaderId ? view.selfGroup?.users.find((user) => user.id === currentLeaderId) : undefined;
    
    const previousLeader = currentGroupLeaderRef.current;
    
    currentGroupLeaderRef.current = currentLeader;

    if (!view.selfGroup) return;

    if (previousLeader && !currentLeader && previousLeader.id !== selfUser.id) {
      setTempInfo({
        message: "Le leader a quitté le groupe",
        userIcon: previousLeader.user.avatar,
        mode: "error",
      });
    }
  }, [view, selfUser]);

  const currentGroupCandidatesInfos = useMemo(() => {
    if (!view?.selfGroup) return undefined;

    const candidates = view.selfGroup.room.leaderCandidates || [];
    const candidatesEscaetUsers = candidates.map((candidate) => escaetUsers.find((user) => user.id === candidate.candidateUserId)).filter((c) => !!c);

    let total = Math.max(view.selfGroup.users.length, view.selfGroup.room.capacity ?? 0);
    let votes = 0;
    candidates.forEach((candidate) => {
      votes += candidate.votes;
      total -= 1; // a candidate can't vote, for now
    });

    const votedUserId = candidates.find((candidate) => candidate.isSelfVote)?.candidateUserId;

    // Both batchCompletion and leaderVoteEnd events will trigger the automatic leader pick
    const batchCompletionDate = view.selfGroup.room.batchCompletionDate;
    const leaderVoteEndDate = view.selfGroup.room.leaderVoteEndDate;
    let nearestDate: Date;
    if (batchCompletionDate && leaderVoteEndDate) {
      nearestDate = batchCompletionDate > new Date() && batchCompletionDate < leaderVoteEndDate ? batchCompletionDate : leaderVoteEndDate;
    } else {
      nearestDate = batchCompletionDate || leaderVoteEndDate || new Date();
    }

    return {
      users: candidatesEscaetUsers,
      votes,
      total,
      votedUserId,
      voteEndDate: nearestDate,
    };
  }, [view, escaetUsers]);

  //////////////////////////////
  const onLeaderUpdate = (groupId: string, userId: string) => {
    const user = escaetUsers.find((user) => user.id === userId);
    if (!user) return;

    const group = escaetGroups.find((group) => group.id === groupId);
    if (!group) return;

    if (user.id === selfUser.id) {
      toastSuccess("Vous êtes le nouveau leader du groupe");
    } else if (group.id === view?.selfGroup?.id) {
      toastSuccess(`${user.firstName} est le nouveau leader du groupe`);
    }
  };

  const onUserEnlisted = (groupId: string, userId: string) => {
    const user = escaetUsers.find((user) => user.id === userId);
    if (!user) return;

    const group = escaetGroups.find((group) => group.id === groupId);
    if (!group) return;

    if (user.id === selfUser.id) {
      toastSuccess(`Vous avez rejoint le groupe ${group.title}`);
    } else if (group.id === view?.selfGroup?.id) {
      toastSuccess(`${user.firstName} a rejoint le groupe`);
    }
  };

  const onUserDelisted = (groupId: string, userId: string) => {
    const user = escaetUsers.find((user) => user.id === userId);
    if (!user) return;

    const group = escaetGroups.find((group) => group.id === groupId);
    if (!group) return;

    if (user.id === selfUser.id) {
      toastSuccess(`Vous avez quitté le groupe ${group.title}`);
    } else if (group.id === view?.selfGroup?.id) {
      toastSuccess(`${user.firstName} a quitté le groupe`);
    }
  };

  const onModeratorCall = (groupId: string, enabled: boolean) => {
    if (groupId !== view?.selfGroup?.id) return;
    // disable for now
    /*if (enabled) toastInfo("Invitation envoyée à l'expert");
    else toastInfo("L'appel a été annulé");*/
  };

  const onModeratorAnswered = (groupId: string, accepted: boolean) => {
    if (groupId !== view?.selfGroup?.id) return;
    if (accepted) {
      toastSuccess("L'expert a accepté l'invitation");
    } else {
      toastInfo("L'expert a refusé l'invitation");
    }
  };

  const onVoteEnd = (groupId: string, leaderUserId: string) => {
    if (groupId !== view?.selfGroup?.id) return;

    const user = escaetUsers.find((user) => user.id === leaderUserId);
    if (!user) return;

    const group = escaetGroups.find((group) => group.id === groupId);
    if (!group) return;

    let message: string;
    if (user.id === selfUser.id) {
      message = "Fin du vote : vous êtes leader du groupe";
    } else {
      message = `Fin du vote : ${user.firstName} est leader du groupe`;
    }

    setTempInfo({
      message,
      userIcon: user.profilePicture,
      mode: "success",
    });
  };

  const onEnlistToGroup = async (roomId: string) => {
    await dispatch(
      socketUserMeetingRoomsActions.enlistInRoom({
        roomId,
      })
    );
  };

  const onJoinUser = (user: GroupUserData) => {
    const userGroup = escaetGroups.find((group) => group.userIds.includes(user.id));
    if (userGroup) {
      onEnlistToGroup(userGroup.id);
    }
  };

  const onApplyAsLeaderCandidate = () => {
    const roomId = view?.selfGroup?.id;
    if (!roomId) return;
    sdkClient.getWebsocketClient().emitter.meeting.applyAsLeaderCandidate({
      roomId,
    });
  };

  const onVoteForLeaderCandidate = (candidateId: string) => {
    const roomId = view?.selfGroup?.id;
    if (!roomId) return;
    sdkClient.getWebsocketClient().emitter.meeting.voteForLeaderCandidate({
      roomId,
      candidateUserId: candidateId,
    });
  };

  const renderLeaderMenuBtn = (icon: React.ReactNode, text: string, onClick?: () => void, enabled = false) => (
    <div
      onClick={onClick}
      style={{
        outline: enabled ? undefined : "1px solid #FFFFFF33",
        background: enabled ? "#0090FF" : undefined,
      }}
      className={styles.leaderButton}
    >
      {icon}
      <Text value={text} style={{ fontSize: 20, fontWeight: 800 }} />
    </div>
  );

  const renderLeaderMenu = () => {
    const renderContent = () => {
      if (tempInfo) {
        return (
          <div style={{ width: "100%", padding: "0px 20px" }}>
            <LeaderMessage message={tempInfo.message} src={tempInfo.userIcon} mode={tempInfo.mode} />
          </div>
        );
      }

      const selfRoom = view.selfGroup?.room;
      if (selfRoom?.leaderUserId) {
        if (selfRoom.leaderUserId === selfUser.id && view.type === "group") {
          const leaderButtonAction = async (action: "callingModerator" | "highlight") => {
            if (action === "callingModerator") {
              if (selfRoom.highlighting !== MeetingRoomHighlightingState.INACTIVE) {
                const success = await stopRoomHighlight(selfRoom.id);
                if (!success) return; // error
              }
              return selfRoom.callingModerator ? cancelModeratorCallFromRoom(selfRoom.id) : callModeratorFromRoom(selfRoom.id);
            }
            if (action === "highlight") {
              if (selfRoom.callingModerator) {
                const room = await cancelModeratorCallFromRoom(selfRoom.id);
                if (!room) return; // error
              }
              return selfRoom.highlighting === MeetingRoomHighlightingState.INACTIVE ? requestRoomHighlight(selfRoom.id) : stopRoomHighlight(selfRoom.id);
            }
          };
          const renderCallingModeratorButton = () => {
            const enabled = selfRoom.callingModerator;
            return renderLeaderMenuBtn(
              <ReactIcons.Bell
                style={{
                  color: enabled ? "#FFFFFFAA" : "#0090ff",
                  width: 24,
                  height: 24,
                }}
              />,
              "Appeler l'expert",
              () => leaderButtonAction("callingModerator"),
              enabled
            );
          };
          const renderHighlightButton = () => {
            const enabled = selfRoom.highlighting !== MeetingRoomHighlightingState.INACTIVE;
            return renderLeaderMenuBtn(
              <ReactIcons.Tableau
                fill={enabled ? "#FFFFFFAA" : "#0090ff"}
                style={{
                  width: 32,
                  height: 32,
                }}
              />,
              "Demander à monter au tableau",
              () => leaderButtonAction("highlight"),
              enabled
            );
          };
          return (
            <div className={styles.leaderButtonsContainer}>
              {renderCallingModeratorButton()}
              {renderHighlightButton()}
            </div>
          );
        }
        return null;
      } else if (currentGroupCandidatesInfos) {
        const alreadyCandidated = currentGroupCandidatesInfos.users.some((candidate) => candidate.id === selfUser.id);
        return (
          <>
            {alreadyCandidated ||
              renderLeaderMenuBtn(<ReactIcons.CrownMin style={{ width: 24, height: 24, color: "#3BD2C9" }} />, "Me déclarer leader", onApplyAsLeaderCandidate)}
            <div style={{ width: "100%", padding: "0px 20px" }}>
              <LeaderVote //
                candidates={currentGroupCandidatesInfos.users}
                voteCount={currentGroupCandidatesInfos.votes}
                totalCount={currentGroupCandidatesInfos.total}
                onVote={onVoteForLeaderCandidate}
                votedUserId={currentGroupCandidatesInfos.votedUserId}
                voteEndDate={currentGroupCandidatesInfos.voteEndDate ?? new Date()}
              />
            </div>
          </>
        );
      }
    };

    return <div className={styles.groupLeaderMenu}>{renderContent()}</div>;
  };

  const renderHeader = () => {
    const group = view.selfGroup;
    let notchText: string = `Groupe ${group?.room.name ?? ""}`;
    let NotchIcon: React.FC<React.SVGProps<SVGElement>> = ReactIcons.GroupMin;
    let rightContent: React.ReactNode;

    const renderInfo = (message: string, icon?: React.ReactNode) => {
      return (
        <div className={styles.groupModeHeaderInfoContainer}>
          {message}
          {icon}
        </div>
      );
    };

    if (group?.room?.highlighting === MeetingRoomHighlightingState.PENDING_REQUEST) {
      rightContent = renderInfo("Demande à monter au tableau en attente", <ReactIcons.Tableau width={18} height={18} fill={"#FFFFFFB2"} />);
    } else if (group?.room?.callingModerator) {
      rightContent = renderInfo("Appel de l'expert en attente", <ReactIcons.Bell width={18} height={18} fill={"#FFFFFFB2"} />);
    }

    if (view.type === "activity_all_leaders") {
      notchText = "Tous les leaders";
      NotchIcon = ReactIcons.CrownFour;
      rightContent = null;
    } else if (view.type === "activity_one_leader") {
      notchText = "Discussion avec l'expert";
      NotchIcon = ReactIcons.PedagoHeadphoneNotFilled;
      rightContent = null;
    }

    if (populatedMeeting.isPedagogy) {
      notchText = "Pédagogie";
      NotchIcon = ReactIcons.PedagoHeadphoneNotFilled;
      rightContent = null;
    }

    return (
      <div className={styles.groupModeHeader}>
        <div className={styles.groupModeHeaderLeftContent}></div>
        <div className={styles.groupModeHeaderCenterContent}>
          <NotchIcon className={styles.groupModeHeaderIcon} />
          <div className={styles.groupModeHeaderTitle}>{notchText}</div>
        </div>
        <div className={styles.groupModeHeaderRightContent}>{rightContent}</div>
      </div>
    );
  };

  const renderCurrentGroup = () => {
    if (!view.selfGroup) return null;
    const studentSessions = view.students.filter((data) => data.video).map((data) => data.session);
    const expertSessions = view.experts.filter((data) => data.video).map((data) => data.session);
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {renderHeader()}
        <div
          style={{
            width: "100%",
            flex: 1,
            minHeight: 0,
            padding: 20,
          }}
        >
          <RemotesToExpertsContainer
            remoteSessions={studentSessions}
            expertSessions={expertSessions}
            self={{
              present: true,
              videoStream: selfVideoStream,
              screens: true,
            }}
            layout={layout}
            showRemoteGroup={view.type === "activity_all_leaders"}
          />
        </div>
        {renderLeaderMenu()}
      </div>
    );
  };

  const renderMainMessage = (value: string, loading?: boolean) => {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "white",
          fontSize: 24,
          fontWeight: 800,
          gap: 10,
        }}
      >
        <Text value={value} />
        {loading && <ReactIcons.Spinner2 style={{ width: 36, height: 36 }} />}
      </div>
    );
  };

  const renderGroupSelection = () => {
    return (
      <div className={styles.backgroundGroupChoice}>
        <div className={styles.groupChoiceHeader}>
          <div className={styles.groupChoiceHeaderLeft}>
            <div className={styles.groupChoiceHeaderLeftTitle}>
              <img src={Icons.ApprenantChoiceGroup} alt="question" />
              <div>Choisir un groupe</div>
            </div>
            <CountdownTimer style={{ color: "#FFFFFFB2" }} targetDate={rooms.groups[0].room.batchCompletionDate ?? new Date()} />
          </div>
          <Touchable
            onClick={() => setSearchOpen((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              backgroundColor: "#22222240",
              padding: "10px 20px",
              borderRadius: 12,
            }}
          >
            <div
              style={{
                color: "#FFF",
                fontSize: 24,
                fontWeight: 800,
              }}
            >
              Chercher un participant
            </div>
            <ReactIcons.Loupe style={{ color: "#FFFFFFB2", width: 20 }} />
          </Touchable>
        </div>
        <div className={styles.groupChoices}>
          <div className={styles.groupChoicesCentered}>
            {escaetGroups.map((group, index) => (
              <ChoiceGroupCard
                key={index}
                id={group.id}
                name={group.title}
                member={group.userIds?.length || 0}
                total={group.capacity || 0}
                isSelected={selectedGroupId === group.id}
                onSelect={setSelectedGroupId}
              />
            ))}
          </div>
        </div>
        <div className={styles.groupChoiceFooter}>
          <ButtonFinal
            disabled={!selectedGroupId}
            text="Rejoindre le groupe"
            background="#115890"
            cx="!text-white !py-[15px] !px-[40px] !rounded-[10px] !font-bold !text-[24px]"
            onClick={() => {
              selectedGroupId && onEnlistToGroup(selectedGroupId);
            }}
          />
        </div>
        {searchOpen && (
          <GroupUserSearch
            sortMethod={sortMethod}
            onSortMethodChange={setSortMethod}
            style={{
              width: "min(700px, 80%)",
            }}
            groups={escaetGroups}
            groupUsers={escaetUsers}
            onClose={() => setSearchOpen(false)}
            onJoinUser={onJoinUser}
          />
        )}
      </div>
    );
  };

  if (view.type === "normal") {
    if (rooms.groups.length === 0 || !rooms.groups.some((group) => group.room.status !== MeetingRoomStatus.Closed)) {
      return <GoTo page={PageMasterMode.MEETING_DASHBOARD} params={{ id: meeting.id }} />;
    }
    return renderGroupSelection();
  } else if (view.selfGroup) {
    return renderCurrentGroup();
  } if (view.type === "highlight") {
    return <GoTo page={PageMasterMode.MEETING_HIGHLIGHT} params={{ id: meeting.id }} />;
  }
  return renderMainMessage("L'expert va vous placer dans un groupe", true);
};
